/**
 * What a USER sees first: the template as a form, not as a property inspector.
 *
 * An admin builds the artwork and thinks in elements, so the panel is a stack of
 * style sections keyed off whatever is selected. Someone filling a business card
 * in has the opposite problem — they open the editor and are told to "select an
 * editable element on the canvas", with no way to know which parts are theirs to
 * change without hunting around the canvas.
 *
 * So this lists every editable element up front, frame by frame, with the one
 * control each type actually needs: a text box for text, a Replace button for a
 * picture, the encoded value for a code. Everything else — fonts, colours,
 * effects, position — sits behind the ⚙ on each card, which opens exactly the
 * panel the admin sees for that element.
 *
 * Selection stays in sync both ways: picking a card selects it on canvas, and
 * clicking something on canvas scrolls its card into view and rings it.
 */
import { useEffect, useRef } from 'react';
import { isSectionLocked } from '../../schema/sectionLocks';
import {
    IconAdjustmentsHorizontal, IconUpload, IconArrowsSplit2, IconLink,
} from '@tabler/icons-react';
import { ELEMENT_TYPES, contentLockKey } from '../../schema/documentSchema';
import { PanelSection, T, figInputCls } from '../../ui/figma';
import { elementIcon } from '../../ui/elementIcons';

/** Breathing room left above a card pulled to the top of the panel. */
const CARD_TOP_GAP = 12;

/**
 * Thumbnails sit on a checkerboard, not on a flat panel colour: half the layers in
 * an imported PSD are white artwork or a transparent cut-out, and on a plain white
 * tile both look like an empty box the user is missing something in.
 */
const CHECKER_CLS = 'bg-white dark:bg-[#2c2c2c]';
const CHECKER_STYLE = {
    backgroundImage:
        'linear-gradient(45deg, rgba(0,0,0,.08) 25%, transparent 25%, transparent 75%, rgba(0,0,0,.08) 75%),'
        + 'linear-gradient(45deg, rgba(0,0,0,.08) 25%, transparent 25%, transparent 75%, rgba(0,0,0,.08) 75%)',
    backgroundSize: '8px 8px',
    backgroundPosition: '0 0, 4px 4px',
};

/** Types a user can meaningfully fill in from a list. */
const LISTED_TYPES = new Set([
    ELEMENT_TYPES.TEXT,
    ELEMENT_TYPES.PLACEHOLDER,
    ELEMENT_TYPES.IMAGE,
    ELEMENT_TYPES.QRCODE,
    ELEMENT_TYPES.BARCODE,
    ELEMENT_TYPES.TABLE,
]);

/**
 * An element this user is allowed to touch. `editableByUser === false` is the
 * admin's "this is artwork, hands off"; a locked element is the same answer in
 * the layers panel's words.
 */
export function isUserEditable(el) {
    return !!el
        && LISTED_TYPES.has(el.type)
        && el.visible !== false
        && !el.locked
        && el.editableByUser !== false;
}

/**
 * An element with something to FILL IN — which is a narrower question than
 * whether the user may touch it at all.
 *
 * A layer whose Text content section is locked still belongs to the user (they
 * may recolour it, move it, whatever the admin left open) but its words are not
 * theirs to change, so a card asking them to type into it would be a dead box.
 * The store refuses the write either way; this is what keeps the list honest.
 */
export function isUserFillable(el) {
    if (!isUserEditable(el)) return false;
    // A photo's words are its picture: the Image lock is what says the user may
    // not swap it, so a card offering a Replace button would be a dead one. A
    // code and a table are the same story under their own section — the store
    // refuses those writes too, and this is what keeps the list honest.
    return !isSectionLocked(el, contentLockKey(el), 'user');
}

const isTextLike = (el) => el.type === ELEMENT_TYPES.TEXT || el.type === ELEMENT_TYPES.PLACEHOLDER;

/** A short, honest label for a card — the element's name, or its own text. */
function cardLabel(el) {
    const name = String(el.name || '').trim();
    if (name && name.toLowerCase() !== el.type) return name;
    if (isTextLike(el)) {
        const text = String(el.content || '').trim();
        return text ? text.slice(0, 28) : 'Text';
    }
    return name || el.type;
}

function TextCard({ el, onChange }) {
    return (
        <textarea
            value={el.content ?? ''}
            onChange={(e) => onChange({ content: e.target.value })}
            onKeyDown={(e) => e.stopPropagation()} // editor shortcuts must not see typing
            rows={Math.min(6, Math.max(2, String(el.content || '').split('\n').length))}
            placeholder="Type here…"
            className={`${figInputCls} !h-auto py-1.5 leading-relaxed resize-y`}
        />
    );
}

/**
 * A photo row IS the thumbnail — click it and the file picker opens.
 *
 * The button used to be a full-width bar under a small preview, which read as the
 * loudest thing in a panel of twenty of them while the picture it was about was a
 * 12px square. The picture is the target now and the words are gone: the hover
 * state on the thumbnail says what a click does.
 *
 * The picker opens on POINTER DOWN, not on click. The card selects on pointerdown
 * too, and a selection can move this row (the panel scrolls the selected card into
 * view), which lands the pointerup somewhere else entirely — and a click event that
 * never fires is a button that silently does nothing.
 */
function ImageCard({ el, label, onReplace, actions }) {
    const inputRef = useRef(null);
    return (
        <div className="flex items-center gap-2.5">
            <button
                type="button"
                onPointerDown={(e) => {
                    // Not stopped: the card still selects, so the canvas rings the
                    // picture being replaced while the file dialog is open.
                    e.preventDefault();
                    inputRef.current?.click();
                }}
                title={el.src ? 'Replace photo' : 'Add photo'}
                className={`group/thumb relative h-11 w-11 shrink-0 rounded-[5px] border ${T.border}
                    overflow-hidden ${CHECKER_CLS} focus:outline-none focus-visible:ring-1 focus-visible:ring-[#0d99ff]`}
                style={CHECKER_STYLE}
            >
                {el.src ? (
                    <img src={el.src} alt="" className="relative h-full w-full object-contain" />
                ) : null}
                {/* The hint only appears over the picture it would replace, so the
                    panel stays quiet until the pointer says otherwise. */}
                <span
                    className="absolute inset-0 flex items-center justify-center bg-black/50 text-white
                        opacity-0 transition-opacity group-hover/thumb:opacity-100"
                >
                    <IconUpload size={15} stroke={1.75} />
                </span>
            </button>

            <div className={`flex-1 min-w-0 truncate text-[11px] font-medium ${T.text}`}>{label}</div>

            {actions}

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                    // Copy the FileList BEFORE clearing the input: it is live, and
                    // the reset empties the very list this handler is holding — the
                    // reason picking a photo here did nothing at all.
                    const files = Array.from(e.target.files || []);
                    e.target.value = ''; // picking the SAME file again must still fire
                    if (files.length) onReplace(files);
                }}
            />
        </div>
    );
}

function ValueCard({ el, onChange, placeholder }) {
    return (
        <input
            value={el.content ?? ''}
            onChange={(e) => onChange({ content: e.target.value })}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder={placeholder}
            className={figInputCls}
        />
    );
}

export default function UserContentPanel({
    frames = [],
    elements = [],
    scrollRef = null,
    selectedId = null,
    focusId = null,
    onSelect,
    onAdvanced,
    onChangeElement,
    onReplaceImage,
    onDetach,
}) {
    const cardRefs = useRef(new Map());

    // Canvas is the other half of this conversation: clicking a photo out there
    // should bring its card here, not leave the user scrolling for it.
    //
    // The card is pulled to the TOP of the panel rather than merely into view:
    // "nearest" leaves it wherever it already was — often the last row, with the
    // fields the user came to fill in hidden below the fold — while the top is
    // where the eye lands after clicking. Scrolls the panel itself, not the
    // element into the document, so nothing else on the page moves.
    //
    // Driven by `focusId`, which only a CANVAS pick sets: scrolling on a click
    // inside the panel would move the row out from under that very click.
    useEffect(() => {
        if (!focusId) return;
        const card = cardRefs.current.get(focusId);
        if (!card) return;
        const box = scrollRef?.current;
        if (!box) {
            card.scrollIntoView({ block: 'start', behavior: 'smooth' });
            return;
        }
        const top = box.scrollTop + (card.getBoundingClientRect().top - box.getBoundingClientRect().top);
        box.scrollTo({ top: Math.max(0, top - CARD_TOP_GAP), behavior: 'smooth' });
    }, [focusId, scrollRef]);

    const editable = elements.filter(isUserFillable);
    if (!editable.length) {
        return (
            <div className={`px-4 py-3 text-[11px] leading-relaxed ${T.textSoft}`}>
                This template has nothing to fill in — download it as it is, or ask
                whoever made it to unlock the parts you need.
            </div>
        );
    }

    // Frames become headings only when there is more than one, so a single-sided
    // card doesn't grow a pointless "Frame 1" label.
    const byFrame = frames
        .map((f) => ({ frame: f, items: editable.filter((e) => e.frameId === f.id) }))
        .filter((g) => g.items.length);
    const orphans = editable.filter((e) => !frames.some((f) => f.id === e.frameId));
    if (orphans.length) byFrame.push({ frame: null, items: orphans });

    /**
     * One card per linked group, one per loose element.
     *
     * The admin ties the places that must say the same thing together, so the
     * user should see the FIELD, not the three layers behind it — otherwise they
     * type the bride's name three times and one of them ends up misspelt.
     */
    // A group spans frames — the back of a card names the couple too — so the
    // "already shown" set lives across the whole panel, not per frame, or the
    // same field would appear under every frame that holds a member.
    const shownGroups = new Set();
    const cardsFor = (items) => {
        const out = [];
        const doneKeys = shownGroups;
        items.forEach((el) => {
            const key = !el.contentDetached ? el.contentKey : null;
            if (!key) { out.push({ kind: 'one', el }); return; }
            if (doneKeys.has(key)) return;
            doneKeys.add(key);
            // Members can live on other frames — the back of a card names the
            // couple too — so the group is gathered from the whole document.
            const members = elements.filter(
                (e) => e.contentKey === key && !e.contentDetached && isUserFillable(e),
            );
            out.push(members.length > 1
                ? { kind: 'group', key, members, el: members[0] }
                : { kind: 'one', el });
        });
        return out;
    };

    const renderGroupCard = ({ key, members, el }) => {
        const selected = members.some((m) => m.id === selectedId);
        const isImage = el.type === ELEMENT_TYPES.IMAGE;
        const isCode = el.type === ELEMENT_TYPES.QRCODE || el.type === ELEMENT_TYPES.BARCODE;
        const isTable = el.type === ELEMENT_TYPES.TABLE;
        return (
            <div
                key={key}
                ref={(node) => {
                    members.forEach((m) => {
                        if (node) cardRefs.current.set(m.id, node);
                        else cardRefs.current.delete(m.id);
                    });
                }}
                onPointerDown={() => onSelect?.(el.id)}
                className={`rounded-[6px] border p-2 flex flex-col gap-1.5 transition-colors
                    ${selected ? 'border-[#0d99ff] ring-1 ring-[#0d99ff]/40' : `${T.border}`}`}
            >
                <div className="flex items-center gap-1.5">
                    <IconLink size={13} className={`shrink-0 ${T.textSoft}`} stroke={1.75} />
                    <span className={`flex-1 min-w-0 truncate text-[11px] font-medium ${T.text}`}>
                        {el.contentLabel || cardLabel(el)}
                    </span>
                    <span className={`shrink-0 text-[10px] tabular-nums ${T.textFaint}`}>
                        {members.length} places
                    </span>
                    <button
                        type="button"
                        title={isImage ? 'Use a different photo in each place'
                            : isTable ? 'Fill each table separately'
                                : isCode ? 'Give each code its own value'
                                    : 'Write each place separately'}
                        onClick={(e) => {
                            e.stopPropagation();
                            members.forEach((m) => onDetach?.(m.id, true));
                        }}
                        className={`h-6 w-6 shrink-0 flex items-center justify-center rounded-[5px]
                            ${T.hoverBg} text-black/60 dark:text-white/60`}
                    >
                        <IconArrowsSplit2 size={14} stroke={1.75} />
                    </button>
                    <button
                        type="button"
                        title="All settings for this element"
                        onClick={(e) => { e.stopPropagation(); onAdvanced?.(el.id); }}
                        className={`h-6 w-6 shrink-0 flex items-center justify-center rounded-[5px]
                            ${T.hoverBg} text-black/60 dark:text-white/60`}
                    >
                        <IconAdjustmentsHorizontal size={14} stroke={1.75} />
                    </button>
                </div>
                {isImage ? (
                    // The card's own header already names the field, so the
                    // thumbnail row says what a click does instead of repeating it.
                    <ImageCard
                        el={el}
                        label={el.src ? 'Change photo' : 'Add photo'}
                        onReplace={(files) => onReplaceImage(el.id, files)}
                    />
                ) : isTable ? (
                    <p className={`text-[10px] leading-snug ${T.textFaint}`}>
                        {el.rows || 0} × {el.cols || 0} table — edit the cells in its
                        full settings and every linked copy follows.
                    </p>
                ) : isCode ? (
                    // A code's value is one line — a URL or a number — so it gets the
                    // same single-line box a loose code gets, not a paragraph area.
                    <ValueCard
                        el={el}
                        placeholder={el.type === ELEMENT_TYPES.QRCODE ? 'https://…' : '123456789012'}
                        onChange={(patch) => onChangeElement(el.id, patch)}
                    />
                ) : (
                    <TextCard el={el} onChange={(patch) => onChangeElement(el.id, patch)} />
                )}
                <p className={`text-[10px] leading-snug ${T.textFaint}`}>
                    Used in {members.length} places — they all change together, each keeping
                    its own {isImage ? 'size, crop and border'
                        : isTable ? 'fonts, colours and borders'
                            : isCode ? 'size, colours and code style'
                                : 'size and colour'}.
                </p>
            </div>
        );
    };

    const renderCard = (el) => {
        const Icon = elementIcon(el);
        const selected = el.id === selectedId;
        const isImage = el.type === ELEMENT_TYPES.IMAGE;
        const advancedBtn = (
            <button
                type="button"
                title="All settings for this element"
                onClick={(e) => { e.stopPropagation(); onAdvanced?.(el.id); }}
                className={`h-6 w-6 shrink-0 flex items-center justify-center rounded-[5px]
                    ${T.hoverBg} text-black/60 dark:text-white/60`}
            >
                <IconAdjustmentsHorizontal size={14} stroke={1.75} />
            </button>
        );
        return (
            <div
                key={el.id}
                ref={(node) => {
                    if (node) cardRefs.current.set(el.id, node);
                    else cardRefs.current.delete(el.id);
                }}
                onPointerDown={() => onSelect?.(el.id)}
                className={`rounded-[6px] border p-2 flex flex-col gap-1.5 transition-colors
                    ${selected ? 'border-[#0d99ff] ring-1 ring-[#0d99ff]/40' : `${T.border}`}`}
            >
                {/* A photo card carries its own name — the thumbnail row IS the
                    header, so it doesn't get a second one above it. */}
                {!isImage && (
                    <div className="flex items-center gap-1.5">
                        <Icon size={13} className={`shrink-0 ${T.textSoft}`} stroke={1.75} />
                        <span className={`flex-1 min-w-0 truncate text-[11px] font-medium ${T.text}`}>
                            {cardLabel(el)}
                        </span>
                        {advancedBtn}
                    </div>
                )}

                {isTextLike(el) && (
                    <TextCard el={el} onChange={(patch) => onChangeElement(el.id, patch)} />
                )}
                {isImage && (
                    <ImageCard
                        el={el}
                        label={cardLabel(el)}
                        actions={advancedBtn}
                        onReplace={(files) => onReplaceImage(el.id, files)}
                    />
                )}
                {el.contentKey && el.contentDetached && (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onDetach?.(el.id, false); }}
                        className={`self-start flex items-center gap-1 text-[10px] ${T.textSoft} hover:underline`}
                    >
                        <IconLink size={12} stroke={1.75} />
                        Follow “{el.contentLabel || 'the linked field'}” again
                    </button>
                )}
                {el.type === ELEMENT_TYPES.QRCODE && (
                    <ValueCard
                        el={el}
                        placeholder="https://…"
                        onChange={(patch) => onChangeElement(el.id, patch)}
                    />
                )}
                {el.type === ELEMENT_TYPES.BARCODE && (
                    <ValueCard
                        el={el}
                        placeholder="123456789012"
                        onChange={(patch) => onChangeElement(el.id, patch)}
                    />
                )}
                {el.type === ELEMENT_TYPES.TABLE && (
                    <p className={`text-[10px] leading-snug ${T.textFaint}`}>
                        {el.rows || 0} × {el.cols || 0} table — edit the cells on canvas,
                        or open all settings.
                    </p>
                )}
            </div>
        );
    };

    return (
        <>
            {byFrame.map(({ frame, items }) => (
                <PanelSection
                    key={frame?.id || 'loose'}
                    title={byFrame.length > 1 ? (frame?.name || 'Elsewhere') : 'Fill in your details'}
                >
                    <div className="flex flex-col gap-2 py-0.5">
                        {cardsFor(items).map((c) => (
                            c.kind === 'group' ? renderGroupCard(c) : renderCard(c.el)
                        ))}
                    </div>
                </PanelSection>
            ))}
            <div className={`px-4 py-2.5 text-[10px] leading-snug ${T.textFaint}`}>
                Everything you can change is listed here. The slider icon on a card opens
                its full settings — fonts, colours, size and position.
            </div>
        </>
    );
}
